import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import url from '../../../../serverUrl';
import { api as axios } from '../../../../api';
import {
  connectWallet,
  describeError,
  fmtPrice,
  fmtQty,
  fmtTime,
  fmtUsd,
  lsGet,
  lsSet,
  num
} from './format';
import { Logo } from './PairPicker';
import { useToast } from './Toast';
import './pro.css';

// ---------------------------------------------------------------------------
// Account tables under the chart: Balances, Open Orders (cancel), Order
// History, Trade History and Wallet Activity, with a "this market only"
// filter. Data: /api/v2/wallet/balance (passed in), /api/v2/wallet/
// transactions (orders + fills) and /api/v2/wallet/rawtransactions.
// ---------------------------------------------------------------------------

const OPEN = new Set(['non-executed', 'executing', 'true', 'pending', 'open']);
const isOpen = (o) => OPEN.has(String(o.status ?? 'true').toLowerCase());

function Empty({ children }) {
  return <div className="x-empty">{children}</div>;
}

function NeedWallet() {
  return (
    <div className="x-empty">
      <p>Connect your wallet to see your balances, orders and trades.</p>
      <button type="button" className="x-of-submit x-connect x-inline" onClick={connectWallet}>
        Connect Wallet
      </button>
    </div>
  );
}

// Perps and Predictions aren't live yet: show the table they will fill so
// the layout is final, with an honest empty state.
const SOON_COLS = {
  perps: [
    ['Market'], ['Side'], ['Size', 1], ['Entry price', 1], ['Mark price', 1],
    ['Unrealized PnL (ROE)', 1], ['Liq. price', 1], ['Margin', 1], ['Funding', 1]
  ],
  predictions: [
    ['Market'], ['Outcome'], ['Shares', 1], ['Avg price', 1], ['Current price', 1],
    ['Value', 1], ['PnL', 1], ['Resolves']
  ]
};
function SoonTable({ kind }) {
  return (
    <>
      <table className="x-table">
        <thead>
          <tr>
            {SOON_COLS[kind].map(([label, right]) => (
              <th key={label} className={right ? 'x-r' : undefined}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
      </table>
      <div className="x-empty">
        {kind === 'perps'
          ? 'Perpetual futures are coming to HootDex. Your open positions will appear here.'
          : 'Prediction markets are coming to HootDex. Your positions will appear here.'}
      </div>
    </>
  );
}

// "12 trades · last 5m" - real fills only, from the tape already loaded.
function MarketPace({ rows }) {
  if (!rows || !rows.length) return null;
  const now = Date.now();
  const recent = rows.filter((t) => now - Number(new Date(Number(t.timestamp) || t.timestamp)) <= 5 * 60 * 1000);
  const vol = recent.reduce((s, t) => s + Math.abs(num(t.amount) ?? 0) * (num(t.price) || 0), 0);
  return (
    <span className="x-pace">
      <i />
      {recent.length} trade{recent.length === 1 ? '' : 's'} · {fmtUsd(vol, { compact: true })} in the last 5m
    </span>
  );
}

function Th({ children, right }) {
  return <th className={right ? 'x-r' : undefined}>{children}</th>;
}

export default function AccountPanel({ walletAssets, auth_token, market, compact }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { WalletInfo } = useSelector((s) => s);
  const [tab, setTab] = useState(() => lsGet('hd_account_tab2', market === null ? 'balances' : 'market'));
  const [mkt, setMkt] = useState(null);
  const seenMkt = React.useRef(new Set());
  const emptyMkt = React.useRef(0);
  const [freshMkt, setFreshMkt] = useState(new Set());
  const [onlyMarket, setOnlyMarket] = useState(() => lsGet('hd_only_market', false));
  const [hideSmall, setHideSmall] = useState(() => lsGet('hd_hide_small', false));
  const [tx, setTx] = useState(null);
  const [raw, setRaw] = useState(null);
  const [busy, setBusy] = useState({});
  const connected = Boolean(auth_token);
  const headers = useMemo(() => ({ Authorization: `Bearer ${auth_token}` }), [auth_token]);

  const loadTx = useCallback(() => {
    if (!connected) return Promise.resolve();
    return axios
      .get(`${url}/api/v2/wallet/transactions`, { headers, timeout: 20000 })
      .then((res) => setTx(res.data || {}))
      .catch(() => setTx((t) => t || {}));
  }, [connected, headers]);

  const loadRaw = useCallback(() => {
    if (!connected) return Promise.resolve();
    return axios
      .get(`${url}/api/v2/wallet/rawtransactions`, { headers, timeout: 20000 })
      .then((res) => setRaw(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRaw((r) => r || []));
  }, [connected, headers]);

  // Orders/fills: poll while visible, and right after an order is placed.
  useEffect(() => {
    if (!connected) return undefined;
    let timer;
    let cancelled = false;
    const tick = () =>
      loadTx().finally(() => {
        if (!cancelled) timer = setTimeout(tick, 10000);
      });
    tick();
    const onChange = () => loadTx();
    window.addEventListener('hd:orders-changed', onChange);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('hd:orders-changed', onChange);
    };
  }, [connected, loadTx]);

  useEffect(() => {
    if (tab === 'activity') loadRaw();
  }, [tab, loadRaw]);

  // Public tape for the current market (same feed the old Market Trades
  // table used), refreshed every 2s while the tab is open.
  useEffect(() => {
    if (tab !== 'market' || !market) return undefined;
    let cancelled = false;
    let timer;
    setMkt(null);
    seenMkt.current = new Set();
    emptyMkt.current = 0;
    const keyOf = (t, i) => t._id || t.hash || `${t.timestamp}-${t.price}-${i}`;
    const tick = () =>
      axios
        .get(`${url}/crypto/marketTrade`, { params: { symbol: market }, timeout: 10000 })
        .then((res) => {
          if (cancelled) return;
          const rows = Array.isArray(res.data) ? res.data : [];
          // Ignore a one-off empty answer (backend hiccup) once we have a tape.
          if (!rows.length && seenMkt.current.size && ++emptyMkt.current < 3) return;
          if (rows.length) emptyMkt.current = 0;
          // Flash only fills that arrived since the last poll.
          const fresh = new Set();
          if (seenMkt.current.size) rows.forEach((t, i) => !seenMkt.current.has(keyOf(t, i)) && fresh.add(keyOf(t, i)));
          rows.forEach((t, i) => seenMkt.current.add(keyOf(t, i)));
          setFreshMkt(fresh);
          setMkt(rows);
        })
        .catch(() => !cancelled && setMkt((m) => m || []))
        .finally(() => {
          if (!cancelled) timer = setTimeout(tick, 2000);
        });
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tab, market]);

  const pick = (k) => {
    setTab(k);
    lsSet('hd_account_tab2', k);
  };

  const inMarket = (sym) => !onlyMarket || !market || sym === market;

  const orders = useMemo(() => {
    const b = (tx?.pending?.bidOrderBook || []).map((o) => ({ ...o, side: 'buy' }));
    const a = (tx?.pending?.askOrderBook || []).map((o) => ({ ...o, side: 'sell' }));
    return [...b, ...a].sort((x, y) => new Date(y.timestamp) - new Date(x.timestamp));
  }, [tx]);
  const openOrders = orders.filter((o) => isOpen(o) && inMarket(o.symbol));
  const pastOrders = orders.filter((o) => !isOpen(o) && inMarket(o.symbol));
  const fills = (tx?.executed || []).filter((t) => inMarket(t.symbol));
  const balances = (walletAssets?.tokens || []).filter(
    (b) => (!hideSmall || (num(b.value) ?? 0) >= 1) && (num(b.amount) ?? 0) !== 0
  );
  const activity = (raw || [])
    .filter((t) => inMarket(t.symbol))
    .sort((x, y) => Number(y.timestamp) - Number(x.timestamp))
    .slice(0, 300);

  const cancel = async (o) => {
    setBusy((b) => ({ ...b, [o._id]: true }));
    try {
      const res = await axios.delete(
        `${url}/api/v2/wallet/${o.side === 'buy' ? 'bidOrder' : 'askOrder'}/${o._id}`,
        { headers }
      );
      toast.success(res.data?.message || 'Order cancelled');
      loadTx();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy((b) => ({ ...b, [o._id]: false }));
    }
  };

  const tabs = [
    ...(market !== null ? [['market', 'Market Trades']] : []),
    ['balances', 'Balances', balances.length],
    ['open', 'Open Orders', openOrders.length],
    ['orders', 'Order History'],
    ['fills', 'Trade History'],
    ['activity', 'Wallet Activity'],
    ['perps', 'Perps', null, true],
    ['predictions', 'Predictions', null, true]
  ];

  return (
    <div className={compact ? 'x-acct x-compact' : 'x-acct'}>
      <div className="x-acct-bar">
        <div className="x-tabs" role="tablist">
          {tabs.map(([k, label, n, soon]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={tab === k ? 'x-tab x-active' : 'x-tab'}
              onClick={() => pick(k)}
            >
              {label}
              {connected && n ? <span className="x-count">{n}</span> : null}
              {soon ? <span className="x-soon">Soon</span> : null}
            </button>
          ))}
        </div>
        <div className="x-acct-filters">
          {tab === 'market' ? (
            <MarketPace rows={mkt} />
          ) : tab === 'perps' || tab === 'predictions' ? null : tab === 'balances' ? (
            <label className="x-check">
              <input
                type="checkbox"
                checked={hideSmall}
                onChange={(e) => {
                  setHideSmall(e.target.checked);
                  lsSet('hd_hide_small', e.target.checked);
                }}
              />
              Hide small balances
            </label>
          ) : (
            <label className="x-check">
              <input
                type="checkbox"
                checked={onlyMarket}
                onChange={(e) => {
                  setOnlyMarket(e.target.checked);
                  lsSet('hd_only_market', e.target.checked);
                }}
              />
              {market ? `${market} only` : 'This market only'}
            </label>
          )}
        </div>
      </div>

      <div className="x-acct-body">
        {tab === 'perps' || tab === 'predictions' ? (
          <SoonTable kind={tab} />
        ) : tab === 'market' ? (
          !mkt ? (
            <Empty>Loading trades…</Empty>
          ) : mkt.length ? (
            <table className="x-table">
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Side</Th>
                  <Th right>Price (USD)</Th>
                  <Th right>Size ({market})</Th>
                  <Th right>Value</Th>
                </tr>
              </thead>
              <tbody>
                {mkt.slice(0, 100).map((t, i) => {
                  const buy = String(t.type).toUpperCase() === 'BUY';
                  const size = Math.abs(num(t.amount) ?? num(t.quantity) ?? 0);
                  const k = t._id || t.hash || `${t.timestamp}-${t.price}-${i}`;
                  return (
                    <tr key={k} className={freshMkt.has(k) ? (buy ? 'x-row-new-buy' : 'x-row-new-sell') : undefined}>
                      <td className="x-muted">{fmtTime(t.timestamp)}</td>
                      <td className={buy ? 'x-up' : 'x-down'}>{buy ? 'Buy' : 'Sell'}</td>
                      <td className={`x-r ${buy ? 'x-up' : 'x-down'}`}>{fmtPrice(t.price)}</td>
                      <td className="x-r">{fmtQty(size)}</td>
                      <td className="x-r">{fmtUsd(size * (num(t.price) || 0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty>No trades in this market yet.</Empty>
          )
        ) : !connected ? (
          <NeedWallet />
        ) : tab === 'balances' ? (
          balances.length ? (
            <table className="x-table">
              <thead>
                <tr>
                  <Th>Asset</Th>
                  <Th right>Total</Th>
                  <Th right>Price</Th>
                  <Th right>USD value</Th>
                  <Th right>Share</Th>
                  <Th right> </Th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => {
                  const total = num(walletAssets?.total) || 0;
                  const share = total ? ((num(b.value) || 0) / total) * 100 : 0;
                  return (
                    <tr key={b.symbol}>
                      <td>
                        <span className="x-asset">
                          <Logo symbol={b.symbol} size={18} />
                          <b>{b.symbol}</b>
                          {b.name && <small className="x-muted">{b.name}</small>}
                        </span>
                      </td>
                      <td className="x-r">{fmtQty(b.amount)}</td>
                      <td className="x-r">{num(b.price) ? fmtPrice(b.price) : '—'}</td>
                      <td className="x-r">{fmtUsd(b.value)}</td>
                      <td className="x-r">
                        <span className="x-share">
                          <span style={{ width: `${Math.min(100, share)}%` }} />
                        </span>
                        {share.toFixed(1)}%
                      </td>
                      <td className="x-r">
                        {b.symbol !== 'PECU' && (
                          <button type="button" className="x-link" onClick={() => navigate(`/${b.symbol}`)}>
                            Trade
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {num(walletAssets?.total) !== null && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td />
                    <td />
                    <td className="x-r">{fmtUsd(walletAssets.total)}</td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          ) : walletAssets ? (
            <Empty>No balances yet.</Empty>
          ) : (
            <Empty>Loading balances…</Empty>
          )
        ) : tab === 'open' || tab === 'orders' ? (
          (() => {
            const list = tab === 'open' ? openOrders : pastOrders;
            if (!tx) return <Empty>Loading orders…</Empty>;
            if (!list.length) return <Empty>{tab === 'open' ? 'No open orders.' : 'No past limit orders.'}</Empty>;
            return (
              <table className="x-table">
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>Market</Th>
                    <Th>Type</Th>
                    <Th>Side</Th>
                    <Th right>Price</Th>
                    <Th right>Size</Th>
                    <Th right>Order value</Th>
                    <Th>Status</Th>
                    {tab === 'open' && <Th right> </Th>}
                  </tr>
                </thead>
                <tbody>
                  {list.map((o) => (
                    <tr key={`${o.side}${o._id}`}>
                      <td className="x-muted">{fmtTime(o.timestamp)}</td>
                      <td>
                        <button type="button" className="x-link" onClick={() => navigate(`/${o.symbol}`)}>
                          {o.symbol}
                        </button>
                      </td>
                      <td>{/MARKET/i.test(o.orderType || '') ? 'Market' : 'Limit'}</td>
                      <td className={o.side === 'buy' ? 'x-up' : 'x-down'}>{o.side === 'buy' ? 'Buy' : 'Sell'}</td>
                      <td className="x-r">{fmtPrice(o.price)}</td>
                      <td className="x-r">{fmtQty(Math.abs(o.quantity))}</td>
                      <td className="x-r">{fmtUsd(Math.abs(o.quantity) * o.price)}</td>
                      <td>
                        <span className={`x-status x-st-${String(o.status).toLowerCase()}`}>
                          {String(o.status) === 'non-executed' ? 'Open' : String(o.status) === 'true' ? 'Open' : String(o.status)}
                        </span>
                      </td>
                      {tab === 'open' && (
                        <td className="x-r">
                          {String(o.status) !== 'executing' && (
                            <button
                              type="button"
                              className="x-btn-cancel"
                              disabled={busy[o._id]}
                              onClick={() => cancel(o)}
                            >
                              {busy[o._id] ? '…' : 'Cancel'}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()
        ) : tab === 'fills' ? (
          !tx ? (
            <Empty>Loading trades…</Empty>
          ) : fills.length ? (
            <table className="x-table">
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Market</Th>
                  <Th>Side</Th>
                  <Th right>Price</Th>
                  <Th right>Size</Th>
                  <Th right>Trade value</Th>
                  <Th>Tx</Th>
                </tr>
              </thead>
              <tbody>
                {fills.slice(0, 300).map((t) => {
                  const buy = String(t.type).toUpperCase() === 'BUY';
                  const size = Math.abs(num(t.amount) ?? num(t.quantity) ?? 0);
                  return (
                    <tr key={t.hash || t._id}>
                      <td className="x-muted">{fmtTime(t.timestamp)}</td>
                      <td>
                        <button type="button" className="x-link" onClick={() => navigate(`/${t.symbol}`)}>
                          {t.symbol}
                        </button>
                      </td>
                      <td className={buy ? 'x-up' : 'x-down'}>{t.type ? (buy ? 'Buy' : String(t.type).toUpperCase() === 'SELL' ? 'Sell' : t.type) : '—'}</td>
                      <td className="x-r">{fmtPrice(t.price)}</td>
                      <td className="x-r">{fmtQty(size)}</td>
                      <td className="x-r">{fmtUsd(size * (num(t.price) || 0))}</td>
                      <td className="x-mono x-muted" title={t.hash}>
                        {t.hash ? `${t.hash.slice(0, 6)}…${t.hash.slice(-4)}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty>No trades yet.</Empty>
          )
        ) : !raw ? (
          <Empty>Loading wallet activity…</Empty>
        ) : activity.length ? (
          <table className="x-table">
            <thead>
              <tr>
                <Th>Time</Th>
                <Th>Asset</Th>
                <Th>Direction</Th>
                <Th right>Amount</Th>
                <Th right>Value</Th>
                <Th>Counterparty</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {activity.map((t) => {
                const me = WalletInfo?.pub_key;
                const out = me ? t.sender === me : num(t.amount) < 0;
                const other = out ? t.recipient : t.sender;
                const amt = Math.abs(num(t.amount) || 0);
                return (
                  <tr key={t._id}>
                    <td className="x-muted">{fmtTime(t.timestamp)}</td>
                    <td>
                      <span className="x-asset">
                        <Logo symbol={t.symbol} size={16} />
                        {t.symbol}
                      </span>
                    </td>
                    <td className={out ? 'x-down' : 'x-up'}>{out ? 'Sent' : 'Received'}</td>
                    <td className="x-r">{fmtQty(amt)}</td>
                    <td className="x-r">{num(t.price) ? fmtUsd(amt * t.price) : '—'}</td>
                    <td className="x-mono x-muted" title={other}>
                      {other ? `${String(other).slice(0, 6)}…${String(other).slice(-4)}` : '—'}
                    </td>
                    <td className="x-muted">{t.publicNote || t.type || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty>No wallet activity yet.</Empty>
        )}
      </div>
    </div>
  );
}
